import React, { useEffect, useState } from "react";
import * as S from "./AdminNoticeDetail.styled";
import { noticeService, NoticeDetail, NoticeType } from "services/noticeService";
import { adminNoticeService } from "services/adminNoticeService";

interface Props {
  noticeId: number;
  onClose: () => void;
  types?: NoticeType[];
  onDeleted?: () => void;
  onUpdated?: () => void;
}

interface FormValues {
  title: string;
  content: string;
  type: string;
}

const AdminNoticeDetail: React.FC<Props> = ({ noticeId, onClose, types = [], onDeleted, onUpdated }) => {
  const [notice, setNotice] = useState<NoticeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormValues | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  const typeMap = Object.fromEntries(types.map((t) => [t.name, t.displayName]));

  const handleEditStart = () => {
    if (!notice) return;
    setForm({ title: notice.title, content: notice.content, type: notice.type ?? "" });
    setIsPreview(false);
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setIsPreview(false);
    setForm(null);
  };

  const handleSave = async () => {
    if (!form) return;
    setIsSaving(true);
    try {
      await adminNoticeService.updateNotice(noticeId, {
        title: form.title,
        content: form.content,
        type: form.type,
      });
      const updated = await noticeService.getNoticeDetail(noticeId);
      setNotice(updated);
      setIsEditing(false);
      setForm(null);
      onUpdated?.();
    } catch {
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("공지사항을 삭제하시겠습니까?")) return;
    setIsDeleting(true);
    try {
      await adminNoticeService.deleteNotice(noticeId);
      onClose();
      onDeleted?.();
    } catch {
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    noticeService
      .getNoticeDetail(noticeId)
      .then(setNotice)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [noticeId]);

  return (
    <S.Overlay onClick={onClose}>
      <S.Panel onClick={(e) => e.stopPropagation()}>

        <S.PanelHeader>
          <S.PanelTitle>{isEditing ? "공지사항 수정" : "공지사항 상세"}</S.PanelTitle>
          <S.HeaderActions>
            {!isEditing && !isLoading && notice && (
              <>
                <S.DeleteButton onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? "삭제 중..." : "삭제"}
                </S.DeleteButton>
                <S.EditButton onClick={handleEditStart}>수정</S.EditButton>
              </>
            )}
            <S.CloseButton onClick={onClose}>✕</S.CloseButton>
          </S.HeaderActions>
        </S.PanelHeader>

        <S.PanelBody>
          {isLoading ? (
            <S.LoadingText>불러오는 중...</S.LoadingText>
          ) : !notice ? (
            <S.LoadingText>정보를 불러올 수 없습니다.</S.LoadingText>
          ) : isEditing && form ? (
            /* ── 수정 폼 ── */
            <>
              <S.FormGroup>
                <S.FormLabel>분류</S.FormLabel>
                <S.FormSelect
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  disabled={isSaving}
                >
                  <option value="">선택하세요</option>
                  {types.map((t) => (
                    <option key={t.name} value={t.name}>{t.displayName}</option>
                  ))}
                </S.FormSelect>
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>제목</S.FormLabel>
                <S.FormInput
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="공지 제목"
                  disabled={isSaving}
                />
              </S.FormGroup>
              <S.FormGroup>
                <S.FormLabel>내용 (HTML)</S.FormLabel>
                <S.TabRow>
                  <S.TabButton
                    active={!isPreview}
                    onClick={() => setIsPreview(false)}
                  >
                    편집
                  </S.TabButton>
                  <S.TabButton
                    active={isPreview}
                    onClick={() => setIsPreview(true)}
                  >
                    미리보기
                  </S.TabButton>
                </S.TabRow>
                {isPreview ? (
                  <S.PreviewBox
                    dangerouslySetInnerHTML={{ __html: form.content }}
                  />
                ) : (
                  <S.FormTextarea
                    rows={16}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="공지 내용을 HTML 형식으로 입력하세요"
                    disabled={isSaving}
                  />
                )}
              </S.FormGroup>
              <S.FormActions>
                <S.CancelButton onClick={handleEditCancel} disabled={isSaving}>
                  취소
                </S.CancelButton>
                <S.SaveButton onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "저장 중..." : "저장"}
                </S.SaveButton>
              </S.FormActions>
            </>
          ) : (
            /* ── 조회 뷰 ── */
            <>
              <S.NoticeTitle>{notice.title}</S.NoticeTitle>
              <S.MetaRow>
                {notice.type && (
                  <S.TypeBadge type={notice.type}>
                    {typeMap[notice.type] ?? notice.type}
                  </S.TypeBadge>
                )}
                <S.DateText>{notice.date}</S.DateText>
              </S.MetaRow>
              <S.ContentBox dangerouslySetInnerHTML={{ __html: notice.content }} />
            </>
          )}
        </S.PanelBody>

      </S.Panel>
    </S.Overlay>
  );
};

export default AdminNoticeDetail;
